import React, { useEffect, useState } from 'react';
import config from '../config';
import { jwtDecode } from 'jwt-decode';
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
  Grid,
} from '@mui/material';
import axios from 'axios';

const AdminParticipants = () => {
  const [search, setSearch] = useState('');
  // Check admin role using token decoding
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const checkAdmin = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAdmin(false);
        return;
      }
      try {
        const { role } = jwtDecode(token);
        setIsAdmin(typeof role === 'string' && role.toLowerCase() === 'admin');
      } catch (e) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
    window.addEventListener('storage', checkAdmin);
    return () => window.removeEventListener('storage', checkAdmin);
  }, []);
  const [participants, setParticipants] = useState([]);
  const [newName, setNewName] = useState('');
  const [feePaid, setFeePaid] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchParticipants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${config.apiBaseUrl}/participants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setParticipants(res.data);
    } catch (err) {
      // handle error
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchParticipants();
  }, []);

  const handleAdd = async () => {
    if (!newName) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${config.apiBaseUrl}/participants`,
        { name: newName, fee_paid: feePaid },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNewName('');
      setFeePaid(false);
      fetchParticipants();
    } catch (err) {
      // handle error
    }
    setLoading(false);
  };

  const handleToggleFee = async (id, paid) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${config.apiBaseUrl}/participants/${id}`,
        { fee_paid: paid },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchParticipants();
    } catch (err) {
      // handle error
    }
    setLoading(false);
  };

  return isAdmin ? (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Search by Name"
          value={search}
          onChange={e => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          sx={{ width: 300 }}
        />
      </Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Mirmire Saaj Participants
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              label="Name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={3}>
            <FormControlLabel
              control={<Switch checked={feePaid} onChange={e => setFeePaid(e.target.checked)} />}
              label="Fee Paid"
            />
          </Grid>
          <Grid item xs={3}>
            <Button variant="contained" onClick={handleAdd} disabled={loading}>
              Add Participant
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Participant List ({participants.length})
        </Typography>
        {participants.length === 0 ? (
          <Typography>No participants yet.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            {(() => {
              // Filter by search
              const filtered = participants.filter(p =>
                p.name.toLowerCase().includes(search.toLowerCase())
              );
              // Split and sort
              const paid = filtered
                .filter(p => p.fee_paid)
                .sort((a, b) => a.name.localeCompare(b.name));
              const notPaid = filtered
                .filter(p => !p.fee_paid)
                .sort((a, b) => a.name.localeCompare(b.name));
              // Combine for display
              const sorted = [...paid, ...notPaid];
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>#</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Fee Paid</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Toggle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((p, idx) => (
                      <tr
                        key={p.id}
                        style={{
                          background: p.fee_paid ? '#d4edda' : '#f8d7da',
                          color: p.fee_paid ? '#155724' : '#721c24',
                        }}
                      >
                        <td style={{ padding: '8px' }}>{idx + 1}</td>
                        <td style={{ padding: '8px' }}>{p.name}</td>
                        <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                          {p.fee_paid ? 'Yes' : 'No'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <Switch
                            checked={p.fee_paid}
                            onChange={e => handleToggleFee(p.id, e.target.checked)}
                            color={p.fee_paid ? 'success' : 'error'}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </Box>
        )}
      </Paper>
    </Box>
  ) : (
    <Box>
      <Typography variant="h6" color="error">
        Admin access required.
      </Typography>
      <Typography variant="body2" color="secondary">
        Debug info: <br />
        token: {localStorage.getItem('token')}
        <br />
      </Typography>
    </Box>
  );
};

export default AdminParticipants;
